import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/home.css";
import RouteCard from "../components/card";
import AIChatAssistantCard from "../components/card2";
import Card3 from "../components/card3";
import Card4 from "../components/card4";
import { FaFacebookF, FaLinkedinIn, FaInstagram, FaPlus, FaMinus } from "react-icons/fa";

const Home = () => {
    const [activeFaq, setActiveFaq] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const [heroInput, setHeroInput] = useState("");

    const faqs = [
        {
            question: "How does Routechef suggest a confirmed seat route?",
            answer: (
                <>
                    Routechef uses a combination of smart techniques to help you find confirmed seat options—even when direct train routes are waitlisted:
                    <ul>
                        <li>✅ Break journeys using either trains alone or a mix of trains and flights to uncover better availability</li>
                        <li>🚉 Book from the train's origin or to its final destination to access special quotas, while still allowing you to board and deboard at your preferred stations</li>
                        <li>⏱️ Suggest routes with realistic layover windows, factoring in historical delay patterns and commute times between stations and airports</li>
                        <li>📊 Instantly compare availability and prices across train and flight legs to choose the most reliable and cost-effective option</li>
                    </ul>
                    By optimizing routes this way, Routechef helps you beat the waitlist — without needing insider knowledge or hours of searching.
                </>
            ),
        },
        {
            question: "What kind of routes does Routechef suggest?",
            answer: (
                <ul>
                    <li>Direct Train and Flight routes</li>
                    <li>Break journeys (Train + Train) via popular junctions/cities</li>
                    <li>Train + Flight combinations</li>
                    <li>Routes from nearby stations if that increases your chances of a confirmed seat route</li>
                </ul>
            ),
        },
        {
            question: "Can I use Routechef if I already booked a waitlisted ticket?",
            answer: (
                <p>Yes! Routechef is most useful after you get waitlisted. You can find alternate break-journey routes or switch to other transport modes — without cancelling your original ticket until you're sure.</p>
            ),
        },
        {
            question: "Can Routechef help with emergency travel (same-day)?",
            answer: (
                <p>Absolutely. Routechef is ideal for last-minute travelers. If direct options are full, it finds immediate alternatives with confirmed availability — even if it means using a connecting city or switching modes.</p>
            ),
        },
        {
            question: "What makes Routechef better than other route planners?",
            answer: (
                <ul>
                    <li>Unlike traditional apps that stop at "train not available or waitlisted" Routechef: Doubles your chance of finding a confirmed seat</li>
                    <li>Works across train and flight routes</li>
                    <li>Plans smart one-stop connections where trains are full</li>
                    <li>Search routes in your local language (like Hindi, Tamil, etc.) using our WhatsApp/AI assistant — just chat naturally, and we'll handle the rest!</li>
                    <li>Focuses on low-cost options, not just fastest or most expensive ones</li>
                </ul>
            ),
        },
    ];

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            // Instead of changing opacity, we'll adjust the position of the gradient
            const gradientPosition = Math.min(0, -scrollPosition * 0.5);
            document.documentElement.style.setProperty('--gradient-position', `${gradientPosition}px`);
            // Decrease opacity more gradually by using a larger denominator
            const gradientOpacity = Math.max(0, 1 - scrollPosition / 1000);
            document.documentElement.style.setProperty('--gradient-opacity', gradientOpacity);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleHeroSearch = (e) => {
        e.preventDefault();
        navigate("/chat", { state: { fromHero: true, heroInput } });
    };

    return (
        <div className="home-container">
            <div className="gradient-overlay"></div>
            {/* Navigation */}
            <nav className="navbar">
                <div className="logo">
                    <img src="/logo_new.svg" alt="Routechef Logo" />
                    <span><span style={{ color: '#FF5B15', fontWeight: '600' }}>Route</span><span style={{ color: '#0A0A0A', fontWeight: '400' }}>chef</span></span>
                </div>
                <div className="nav-links">
                    <a href="#features">Features</a>
                    <a href="#faqs">FAQs</a>
                    <a href="/contact">Contact</a>
                    <button className="ai-agent-btn" onClick={() => navigate("/chat")}>
                        <img src="/Star1.svg" /> AI Agent
                    </button>
                    <button className="download-btn"
                        onClick={() => window.open("https://play.google.com/store/apps/details?id=indianrailapp.railapp.routechef.myrailapp&hl=en_US&gl=IN", "_blank")}
                    >Download App</button>
                </div>
                <div className="mobile-menu">
                    <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                        <span>⋮</span>
                    </button>
                    {isMobileMenuOpen && (
                        <div className="mobile-dropdown">
                            <a href="#features" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
                            <a href="#faqs" onClick={() => setIsMobileMenuOpen(false)}>FAQs</a>
                            <a href="/contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</a>
                            <button className="ai-agent-btn" onClick={() => {
                                setIsMobileMenuOpen(false);
                                navigate("/chat");
                            }}>
                                <img src="/Star1.svg" /> AI Agent
                            </button>
                            <button className="download-btn" onClick={() => {
                                setIsMobileMenuOpen(false);
                                window.open("https://play.google.com/store/apps/details?id=indianrailapp.railapp.routechef.myrailapp&hl=en_US&gl=IN", "_blank");
                            }}>
                                Download App
                            </button>
                        </div>
                    )}
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    
                    <h1 className="hero-title">No More Waitlists:</h1>
                    <h2 className="hero-subtitle">Get Confirmed Seats with AI-Powered Alternate Routes</h2>
                    <p className="hero-description">AI-powered route planner combining trains and flights for hassle-free travel in India.</p>

                    <div className="search-bar">
                        <form onSubmit={handleHeroSearch} style={{ display: "flex", width: "100%" }}>
                            <div className="search-input" style={{ flex: 1 }}>
                                <img src="/Star1.svg" />
                                <input
                                    type="text"
                                    value={heroInput}
                                    onChange={e => setHeroInput(e.target.value)}
                                    placeholder="Darbhanga se Mumbai jane ke liye kal ki date ke liye routes batao"
                                    style={{
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        width: '100%'
                                    }}
                                />
                            </div>
                            <button className="search-button" type="submit">
                                <img src="/send.svg" />
                            </button>
                        </form>
                    </div>
                </div>
                
            </section>

            {/* Features Section */}
            <section className="features-section" id="features">
                <div className="section-header">
                    <h3>Features</h3>
                    <h2>Why Routechef?</h2>
                    <p>Routechef intelligently combines trains and flights to maximize your chances of confirmed travel.</p>
                </div>

                <RouteCard className="card-style" />

                <AIChatAssistantCard className="card-style" />

                <Card3 className="card-style" />
            </section>

            {/* How It Works Section */}
            <section className="how-it-works">
                <h3>How It Works?</h3>
                <h2>How Routechef Gets You There</h2>
                <div className="steps-container">
                    <div className="step">
                        <div className="step-number">1</div>
                        <h3>Enter Location</h3>
                        <p>Tell us where you're starting and going.</p>
                    </div>
                    <div className="step">
                        <div className="step-number">2</div>
                        <h3>Select Travel Modes</h3>
                        <p>Choose your preferred travel modes: trains, flights.</p>
                    </div>
                    <div className="step">
                        <div className="step-number">3</div>
                        <h3>Get Routes & Book</h3>
                        <p>Explore smart routes with confirmed seat options.</p>
                    </div>
                </div>

                <Card4 className="card-style" />
            </section>

            {/* Testimonials Section */}
            <section className="testimonials-section">
                <h3>Testimonials</h3>
                <h2>Loved by Travelers!</h2>
                <div className="testimonials-container">
                    <div className="testimonial-card">
                        <p className="testimonial-text">Confirmed seat routes are a lifesaver routechef makes planning so easy</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Lucky Dehariya</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">Good break journey details display</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Hrishabh Kishore</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">The app found the nearest station from a small town easily best travel planing app</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Sajit Qureshi</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">Only app I have came through which shows shortest route for break journeys and also have multiple modes of break journey, very helpful in times where direct ticket is not available</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Shri Balaji</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <p className="testimonial-text">Great app very user friendly and break journey also available 😊</p>
                        <div className="testimonial-author">
                            <div className="author-info">
                                <h4>Soumavo Mal</h4>
                                <div className="rating">⭐⭐⭐⭐⭐</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="stats-bar">
                    <div className="stat">
                        <h3>10,000+</h3>
                        <p>routes planned</p>
                    </div>
                    <div className="stat">
                        <h3>90%+</h3>
                        <p>confirmation success rate</p>
                    </div>
                    <div className="stat">
                        <h3>4.3/5</h3>
                        <p>average user rating</p>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq-section" id="faqs">
                <h3>FAQs</h3>
                <h2>Frequently Asked Question's</h2>
                <div className="faq-container">
                    {faqs.map((faq, idx) => (
                        <div
                            className={`faq-item${activeFaq === idx ? " active" : ""}`}
                            key={idx}
                            onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                        >
                            <div className="faq-question">
                                <h4>{faq.question}</h4>
                                <span className="faq-toggle">
                                    {activeFaq === idx ? <FaMinus /> : <FaPlus />}
                                </span>
                            </div>
                            <div className="faq-answer">{faq.answer}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Footer */}
            <footer className="footer" id="contact">
                <div className="footer-content">
                    <div className="footer-brand">
                        <span><span style={{ color: '#FF5B15', fontWeight: '600', fontSize: '24px' }}>Route</span><span style={{ color: '#0A0A0A', fontWeight: '400', fontSize: '24px' }}>chef</span></span>
                        <p style={{ fontWeight: '400', color: '#0A0A0A' }}>Your AI Powered Route Planner for Confirmed Journeys.</p>
                    </div>
                    <div className="footer-links">
                        <h4>Links</h4>
                        <ul>
                            <li><a href="#features">Features</a></li>
                            <li><a href="#faqs">FAQs</a></li>
                            {/* <li><a href="#terms">Terms & Conditions</a></li>
                            <li><a href="#privacy">Privacy policy</a></li> */}
                        </ul>
                    </div>
                    <div className="footer-download">
                        <h4>Scan to Download App</h4>
                        <div className="qr-code">
                            <img src="/qrcode.png" alt="Download QR Code" />
                        </div>
                        <p>Contact: <a href="mailto:info@routechef.com">info@routechef.com</a></p>
                    </div>
                </div>
                <div className="footer-bottom">
                    <div className="social-icons">
                        {/* <a href="#" className="social-icon">X</a> */}
                        <a href="https://www.facebook.com/routechef/" className="social-icon" target="_blank" rel="noopener noreferrer" onMouseUp={e => e.currentTarget.blur()} onTouchEnd={e => e.currentTarget.blur()}>
                            <FaFacebookF />
                        </a>
                        <a href="https://www.linkedin.com/company/105943037" className="social-icon" target="_blank" rel="noopener noreferrer" onMouseUp={e => e.currentTarget.blur()} onTouchEnd={e => e.currentTarget.blur()}>
                            <FaLinkedinIn />
                        </a>
                        {/* <a href="#" className="social-icon">yt</a> */}
                        <a href="https://www.instagram.com/routechef/" className="social-icon" target="_blank" rel="noopener noreferrer" onMouseUp={e => e.currentTarget.blur()} onTouchEnd={e => e.currentTarget.blur()}>
                            <FaInstagram />
                        </a>
                    </div>
                    <p className="copyright">© 2025 Routechef</p>
                    <div className="footer-extra">
                        <span>Made with ❤️ in India</span>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Home;
